import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    forwardRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    OnInit,
    Output,
    Renderer2,
    ChangeDetectionStrategy
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { InfoComponent } from "../info/info.component";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent)
        }
    ],
    selector: "rask-checkbox",
    styleUrls: ["./checkbox.component.scss"],
    templateUrl: "./checkbox.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        RequiredDirective,
        InfoComponent,
        ValidationMessageComponent
    ]
})
export class CheckboxComponent extends ControlValueAccessorBaseComponent implements OnInit {
    @Input() checked: boolean;
    @Output() checkedChange = new EventEmitter<boolean>();
    @Input() fillHeight: boolean;
    @Input() flexResize: boolean;
    focused = false;
    @Input() label: string;
    @Input() partialChecked: boolean;
    @Input() text: string;
    @Input() onBeforeChange: ($event: any) => Promise<boolean>;

    keyDownUnsubscriber: () => void;

    keyUpUnsubscriber: () => void;
    private readonly renderer2 = inject(Renderer2);

    @HostBinding("attr.disabled") get getDisabledState() { return (this.disabled ? "" : null);}

    ngOnInit() {
        super.ngOnInit();
        if (this.checked) {
            this.value = true;
        }
        this.onChangeLocal = (v) => this.checkedChange.emit(v);
    }

    onBlurInput() {
        //Unsubscribar till alla keyevents
        this.focused = false;
        this.keyDownUnsubscriber();
        this.keyDownUnsubscriber = null;
        this.keyUpUnsubscriber();
        this.keyUpUnsubscriber = null;
    }

    @HostListener("click", ["$event"]) onClick(e: any = null) {
        if (this.disabled) {
            return;
        }

        if (!this.onBeforeChange) {
            this.value = !this.value;
        } else {
            this.onBeforeChange(this.value)
                .then(result => {
                    if (result) {
                        this.value = !this.value;
                    }
                });
        }
    }

    onFocusInput() {
        this.focused = true;
        // Lyssnar på keyDown enbart om vi har focus, om "this" är FOCUS och INTE DISABLED samt att key är
        // ett SPACE så stoppar vi ordinarie hantering av eventet = för att förhindra att sidan skrollar
        this.keyDownUnsubscriber = this.renderer2.listen(window, "keydown", (event: KeyboardEvent) => {
            if (this.shouldKeyBeHandled(event)) {
                this.stopKeyEventBubble(event);
            }
        });
        // Lyssnar på keyUp enbart om vi har focus, om "this" är FOCUS och INTE DISABLED samt att key är
        // ett SPACE så stoppar vi ordinarie hantering av eventet = för att toggla this.checked state
        this.keyUpUnsubscriber = this.renderer2.listen(window, "keyup", (event: KeyboardEvent) => {
            if (this.shouldKeyBeHandled(event)) {
                this.stopKeyEventBubble(event);
                this.onClick();
            }
        });
    }

    shouldKeyBeHandled(event: KeyboardEvent) {
        if (this.disabled) {
            return false;
        }
        if (!this.focused) {
            return false;
        }
        return (event.key === " ");
    }

    stopKeyEventBubble(event: KeyboardEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
    }
}
